import React, { useRef, useState } from "react";
import styled from "styled-components";
import File06 from "@untitled-ui/icons-react/build/esm/File06";
import Upload01 from "@untitled-ui/icons-react/build/esm/Upload01";

// #region SCOPED CSS
const StyledUploadCard = styled.div`
    &.upload-card {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 4px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        flex: 1;
        min-width: 300px;
    }

    .card-title {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: var(--overline-font-size, 14px);
        letter-spacing: var(--overline-letter-spacing, 0.015em);
        font-weight: var(--overline-font-weight, 500);
        text-transform: uppercase;
    }

    .card-divider {
        border: none;
        height: 1px;
        background: #3c485a;
        width: 100%;
        margin: 0;
    }

    .upload-body {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: stretch;
        min-height: 110px;
    }

    .dropzone-box {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border: 1px dashed var(--backgrounds-and-lines-n-100, #3c485a);
        border-radius: 4px;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
        justify-content: center;
        width: 130px;
        flex-shrink: 0;
        cursor: pointer;
        transition: border-color 0.2s ease;
        font: inherit;

        &:hover,
        &:focus-visible,
        &.is-dragging {
            border-color: var(--text-and-icons-green-500, #00bb00);
        }
    }

    .dropzone-icon {
        width: 20px;
        height: 20px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
    }

    .dropzone-text {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 11px;
        text-align: center;
        line-height: 14px;
    }

    .file-list-box {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        border-radius: 4px;
        padding: 12px 16px;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 8px;
        justify-content: flex-start;
        overflow-y: auto;
        max-height: 150px;
    }

    .file-item {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 0;
    }

    .file-info {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 12px;
    }

    .file-icon {
        width: 16px;
        height: 16px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
    }

    .file-remove-btn {
        color: var(--text-and-icons-red-500, #ff4d4f);
        background: transparent;
        border: 0;
        padding: 2px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;

        &:hover {
            opacity: 0.8;
        }
    }

    .no-files-text {
        color: var(--text-and-icons-gray-200, #7c868b);
        font-size: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .upload-action-btn {
        background: var(--buttons-accent-green-default, #00bb00);
        color: var(--primary-white-500, #ffffff);
        border-radius: 4px;
        border: none;
        padding: 10px 16px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 130px;

        &:hover {
            background: #009900;
        }
    }

    .file-input {
        display: none;
    }
`;
// #endregion

export default function BIFileUploadCard({
    title = "PREVIOUS SEASON REPORT",
    files: controlledFiles,
    onFilesChanged
}) {
    const fileInputRef = useRef(null);
    const [internalFiles, setInternalFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const files = Array.isArray(controlledFiles) ? controlledFiles : internalFiles;

    const updateFiles = updatedFiles => {
        if (!Array.isArray(controlledFiles)) setInternalFiles(updatedFiles);
        onFilesChanged?.(updatedFiles);
    };

    const addFiles = selectedFiles => {
        if (!selectedFiles.length) return;

        const existingFileIDs = new Set(files.map(file => file.id));
        const newFileItems = selectedFiles
            .map(file => ({
                id: `${file.name}-${file.size}-${file.lastModified}`,
                name: file.name,
                fileObj: file
            }))
            .filter(file => !existingFileIDs.has(file.id));

        if (!newFileItems.length) return;
        updateFiles([...files, ...newFileItems]);
    };

    const handleFileSelect = e => {
        const selectedFiles = Array.from(e.target.files || []);
        addFiles(selectedFiles);
        e.target.value = "";
    };

    const handleDragOver = event => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setIsDragging(true);
    };

    const handleDragLeave = event => {
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsDragging(false);
    };

    const handleDrop = event => {
        event.preventDefault();
        setIsDragging(false);
        addFiles(Array.from(event.dataTransfer.files || []));
    };

    const handleRemoveFile = id => {
        const updated = files.filter(f => f.id !== id);
        updateFiles(updated);
    };

    return (
        <StyledUploadCard className="upload-card">
            <div className="card-title">{title}</div>
            <hr className="card-divider" />

            <div className="upload-body">
                {/* Left Dropzone */}
                <button
                    type="button"
                    className={`dropzone-box ${isDragging ? "is-dragging" : ""}`}
                    aria-label={`Select or drop files for ${title}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}>
                    <Upload01 className="dropzone-icon" />
                    <span className="dropzone-text">Drag and drop files to upload</span>
                </button>
                <input
                    type="file"
                    className="file-input"
                    ref={fileInputRef}
                    multiple
                    onChange={handleFileSelect}
                />

                {/* Right Uploaded File List */}
                <div className="file-list-box">
                    {files.length === 0 ? (
                        <div className="no-files-text">No files uploaded</div>
                    ) : (
                        files.map(file => (
                            <div className="file-item" key={file.id}>
                                <div className="file-info">
                                    <File06 className="file-icon" />
                                    <span title={file.name}>{file.name}</span>
                                </div>
                                <button
                                    type="button"
                                    className="file-remove-btn"
                                    aria-label={`Remove ${file.name}`}
                                    onClick={() => handleRemoveFile(file.id)}>
                                    ✕
                                </button>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Bottom Button */}
            <button type="button" className="upload-action-btn" onClick={() => fileInputRef.current?.click()}>
                UPLOAD FILES
            </button>
        </StyledUploadCard>
    );
}
